/** שיטת פתיחה של משימה ('solution' = המשימה הקודמת דרשה פתרון והוא אושר) */
export type UnlockMethod = 'time' | 'location' | 'manual' | 'solution';

/** מצב משימה מנקודת מבט המשחק */
export type MissionState = 'locked' | 'available' | 'opened';

export interface GeoPoint {
  lat: number;
  lng: number;
}

/**
 * משימה — שיקוף ישיר של שורת Google Sheets + שדות מערכת.
 * השדות הגולמיים נשמרים כפי שנכתבו בגיליון כדי לאפשר סנכרון דו-כיווני נאמן.
 */
export interface Mission {
  /** מזהה פנימי יציב (לא תלוי בשורת הגיליון) */
  id: string;
  /** עמודת # — מזהה אנושי; null אם חסר בגיליון */
  missionNumber: number | null;
  /** עמודת "שעה" — שעת המשימה לתצוגה */
  hour: string;
  /** עמודת "יום" */
  day: string;
  /** עמודת "תאריך" — כפי שנכתב (למשל 25/05/26) */
  date: string;
  /** עמודת "אפשור מיקום" — קישור Google Maps */
  locationUrl: string;
  /** עמודת "אפשור זמן" — שעת אפשור (למשל 17:30); ריק = אין תנאי זמן */
  unlockTimeRaw: string;
  /** עמודת "אפשור ידני" — הערך הגולמי (V וכד') */
  manualRaw: string;
  /** עמודת "המשימה" */
  task: string;
  /** עמודת "הכנות" */
  preparations: string;
  /** עמודת "יוטיוב" */
  youtubeUrl: string;
  /** עמודת "תמונה" — קישור לתמונת המשימה (עמודה K בגיליון); ריק = אין תמונה */
  imageUrl: string;
  /**
   * עמודת "פתרון נדרש" (עמודה L בגיליון): כל טקסט בתא = המשימה דורשת פתרון מהשחקן, והטקסט הוא מה שנדרש
   * (למשל "תמונה של קבלה בעברית") — מוצג לשחקן כהנחיה ומשמש את בדיקת התמונה האוטומטית. ריק = אין פתרון נדרש.
   */
  requiredSolution: string;
  /** קואורדינטות שפוענחו בשרת מקישור המיקום (כולל קישורים מקוצרים) */
  resolvedLat: number | null;
  resolvedLng: number | null;
  /** חותמת אפשור ידני על ידי המנהל (ISO); null = לא אופשר */
  manuallyUnlockedAt: string | null;
  /** גרסה אופטימית לזיהוי התנגשויות */
  version: number;
  /** עדכון אחרון (ISO) */
  updatedAt: string;
  /** מחיקה רכה (ISO); null = פעיל */
  deletedAt: string | null;
  /** האם קיימת התנגשות מול Google Sheets הממתינה להחלטת מנהל */
  conflict?: boolean;
  /** גרסת הגיליון במקרה של התנגשות (להצגת הבדלים) */
  sheetSnapshot?: Partial<Mission> | null;
  /** בעיות תקינות שאותרו בנתוני השורה */
  invalidReasons?: string[];
  /** סדר תצוגה (סדר השורות בגיליון) */
  sortIndex: number;
}

/** תוכן המשימה כפי שהיה ברגע הפתיחה — לא משתנה לעולם */
export interface MissionSnapshot {
  missionNumber: number | null;
  hour: string;
  day: string;
  date: string;
  task: string;
  preparations: string;
  youtubeUrl: string;
  imageUrl: string;
  locationUrl: string;
  /** קואורדינטות תנאי המיקום של המשימה (לא המיקום שבו נפתחה בפועל) — לפין
   * הנוסף במפת הפתיחות; null אם אין תנאי מיקום/לא פוענח */
  resolvedLat: number | null;
  resolvedLng: number | null;
}

/** אירוע פתיחת משימה */
export interface MissionOpening {
  id: string;
  missionId: string;
  missionNumber: number | null;
  snapshot: MissionSnapshot;
  /** מועד הפתיחה בפועל (ISO) */
  openedAt: string;
  /** מיקום GPS בפועל בזמן הפתיחה (לא המיקום שהוגדר כתנאי) */
  latitude: number | null;
  longitude: number | null;
  accuracy: number | null;
  unlockMethod: UnlockMethod;
  /** נוצר במצב בדיקה */
  isTest: boolean;
}

/**
 * פתק ביומן השדה של הסוכן — טקסט ו/או תמונה, נראה גם למנהל (ערוץ תקשורת
 * דו-כיווני: המנהל כבר "כותב" לשחקן דרך תוכן משימות; זהו הכיוון ההפוך).
 */
export interface PlayerMessage {
  id: string;
  text: string;
  /** קישור לתמונה שצורפה (רמז שצולם/הועלה מהגלריה); null = אין תמונה */
  imageUrl: string | null;
  /** נקבע בשרת בזמן הקבלה (ISO) */
  sentAt: string;
  /** המשימה הנוכחית בזמן השליחה, להקשר בלבד — יכול להיות null */
  missionId: string | null;
  missionNumber: number | null;
  /** נשלחה במצב בדיקה */
  isTest: boolean;
  /** מי כתב את הפתק — השחקן או תשובת המנהל */
  sender: 'player' | 'admin';
  /** הודעה דחופה מהמנהל — מוצגת לשחקן בבולטות (באנר) עד שהוא מאשר שראה */
  urgent: boolean;
}

export type SyncOpType =
  | 'CREATE_MISSION'
  | 'UPDATE_MISSION'
  | 'DELETE_MISSION'
  | 'MANUAL_UNLOCK'
  | 'OPEN_MISSION'
  | 'UPDATE_SETTINGS';

/** פעולה בתור הסנכרון של הלקוח */
export interface SyncOperation {
  id: string;
  type: SyncOpType;
  payload: unknown;
  /** גרסת הבסיס שעליה בוצע השינוי (לעריכות) */
  baseVersion?: number;
  createdAt: string;
  attempts: number;
  lastError?: string | null;
}

export interface TestModeSettings {
  enabled: boolean;
  /** זמן מדומה (ISO) — null = זמן אמיתי */
  fakeNowIso: string | null;
  /** מיקום מדומה — null = מיקום אמיתי */
  fakeLat: number | null;
  fakeLng: number | null;
}

export interface AppSettings {
  /** תדירות סנכרון לקוח↔שרת במילישניות; 0 = ידני בלבד */
  syncIntervalMs: number;
  /** תדירות סנכרון שרת↔Google Sheets במילישניות; 0 = ידני בלבד */
  sheetsSyncIntervalMs: number;
  /** רדיוס פתיחת מיקום במטרים */
  gpsRadiusMeters: number;
  /**
   * פתיחה אוטומטית כשמגיע זמן המשימה (תנאי "אפשור זמן"). כבוי = הזמן רק הופך את
   * המשימה ל"ניתנת לפתיחה", והיא נפתחת רק ידנית (אפשור ידני / "פתח עכשיו").
   */
  autoOpenByTime: boolean;
  soundEnabled: boolean;
  animationsEnabled: boolean;
  /** שם המבצע המוצג בטרמינל */
  operationName: string;
  /**
   * אזור הזמן של המבצע (IANA, למשל Europe/Paris). שעות "אפשור זמן" והתאריכים בגיליון נקראים לפיו, וכל השעות
   * באפליקציה מוצגות בו. משנים כשהמבצע מתקיים בחו"ל.
   */
  timeZone: string;
  testMode: TestModeSettings;
  version: number;
  updatedAt: string;
}

export const DEFAULT_SETTINGS: AppSettings = {
  syncIntervalMs: 10_000,
  sheetsSyncIntervalMs: 60_000,
  gpsRadiusMeters: 100,
  autoOpenByTime: true,
  soundEnabled: true,
  animationsEnabled: true,
  operationName: 'מבצע בר-מצווש',
  timeZone: 'Asia/Jerusalem',
  testMode: { enabled: false, fakeNowIso: null, fakeLat: null, fakeLng: null },
  version: 1,
  updatedAt: '1970-01-01T00:00:00.000Z',
};

/** תיקון (patch) חלקי להגדרות — testMode ניתן לעדכון חלקי גם הוא; version/updatedAt מנוהלים על ידי השרת בלבד */
export type SettingsPatch = Partial<Omit<AppSettings, 'testMode' | 'version' | 'updatedAt'>> & {
  testMode?: Partial<TestModeSettings>;
};

/** אפשרויות תדירות סנכרון להצגה בהגדרות */
export const SYNC_INTERVAL_OPTIONS: { label: string; value: number }[] = [
  { label: '0.5 שניות', value: 500 },
  { label: 'שנייה', value: 1_000 },
  { label: '5 שניות', value: 5_000 },
  { label: '10 שניות', value: 10_000 },
  { label: '30 שניות', value: 30_000 },
  { label: 'דקה', value: 60_000 },
  { label: '5 דקות', value: 300_000 },
  { label: 'ידני בלבד', value: 0 },
];
